import cloneDeep from 'lodash/cloneDeep'
export function getDayFromTimestamp(timestamp) {
	const date = new Date(timestamp)
	// Subtract 4 hours
	date.setHours(date.getHours() - 4)
	// Return the date in YYYY-MM-DD format
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function getDaysWithJourneys(trips, surveyDefinitions) {
	console.warn('getDaysWithJourneys', JSON.parse(JSON.stringify(trips)))
	if (trips.length === 0) return []

	// Sort trips by departure timestamp
	trips.sort((a, b) => a.departure_timestamp - b.departure_timestamp)

	// Add absolute indexes
	trips.forEach((trip, index) => {
		trip.index_absolute = index
	})

	// Step 1: Create journeys from trips (without day assignment)
	let journeys = createJourneysFromTrips(trips)

	// Step 2: Merge walking journeys with subsequent public transport journeys
	journeys = mergeWalkingWithPublicTransport(journeys)

	// Step 3: Merge journeys that connect at aeroway terminals
	journeys = mergeJourneysByAerowayTerminal(journeys)

	// Step 4: Assign journeys to days based on journey start time
	const days = assignJourneysToDays(journeys, surveyDefinitions)

	return days
}

function createJourneysFromTrips(trips) {
	if (trips.length === 0) return []

	const journeys = []
	let currentJourney = {
		index: 0,
		trips: [],
		name: '0',
		origin: {},
		destination: {},
	}

	// Define dwell constants in milliseconds
	const DEFAULT_MAX_DWELL = 1000 * 60 * 2 // 2 minutes
	const PUBLIC_TRANSIT_MAX_DWELL = 1000 * 60 * 30 // 30 minutes
	const AIRTRAVEL_MAX_DWELL = 1000 * 60 * 60 * 3 // 3 hours
	const WALKING_PAIR_MAX_DWELL = 1000 * 60 * 10 // 10 minutes

	trips.forEach((trip, index) => {
		// Skip if trip is null or undefined
		if (trip == null) return

		if (index === 0) {
			currentJourney.trips.push(trip)
			return
		}

		const lastTrip = currentJourney.trips[currentJourney.trips.length - 1]
		const timeDifference = trip.departure_timestamp - lastTrip.arrival_timestamp
		const publicTransport = isPublicTransport(trip)
		const distanceGap = calculateLocationDistance(lastTrip.destination, trip.origin)

		// Determine max dwell based on next trip type
		let maxDwell = DEFAULT_MAX_DWELL
		if (trip.activity_name === 'airtravel') {
			maxDwell = AIRTRAVEL_MAX_DWELL
		} else if (publicTransport) {
			maxDwell = PUBLIC_TRANSIT_MAX_DWELL
		}

		// Also extend dwell if the previous trip was airtravel
		if (lastTrip?.activity_name === 'airtravel') {
			maxDwell = Math.max(maxDwell, AIRTRAVEL_MAX_DWELL)
		}

		const isWithinMaxDwell = timeDifference < maxDwell

		// If trip has been splitted because of identified turn point, consider it a separate journey
		const isSplitted = (lastTrip.turn_split && trip.turn_split && lastTrip.turn_index == trip.turn_index) || false

		// Trips that are ABA should in general be a separate journey, if its long enough
		const hasEqualOriginAndDestination = trip.air_distance < 100 && trip.distance > 1000

		// NEW: consecutive walking trips with no POI match belong together
		const isConsecutiveWalking = lastTrip?.activity_name === 'walking' && trip?.activity_name === 'walking'
		const shouldMergeWalkingPair = isConsecutiveWalking && walkingTripsShouldMergeByPOI(lastTrip, trip) && distanceGap < 500 && timeDifference < WALKING_PAIR_MAX_DWELL // NEW

		// If we should continue the current journey
		const shouldContinueJourney = (isWithinMaxDwell && !isSplitted && !hasEqualOriginAndDestination) || shouldMergeWalkingPair

		// Only consider dwell, splits, ABA patterns, and the new walking POI rule
		if (shouldContinueJourney) {
			currentJourney.trips.push(trip)
			console.warn('createJourneysFromTrips: Added to current journey', '#' + trip.index, trip.activity_name, shouldContinueJourney, new Date(trip.departure_timestamp).toLocaleTimeString(), '-', new Date(trip.arrival_timestamp).toLocaleTimeString(), 'timeDiff:', timeDifference / 1000, isWithinMaxDwell, 'split', isSplitted, 'eqOD', hasEqualOriginAndDestination, 'air', trip.air_distance, 'dist', trip.distance)
		} else {
			// Finish current journey and start a new one
			journeys.push(currentJourney)
			currentJourney = createNewJourney(trip, journeys.length)
			console.warn('createJourneysFromTrips: Created new journey', '#' + trip.index, trip.activity_name, shouldContinueJourney, new Date(trip.departure_timestamp).toLocaleTimeString(), '-', new Date(trip.arrival_timestamp).toLocaleTimeString(), 'timeDiff:', timeDifference / 1000, isWithinMaxDwell, 'split', isSplitted, 'eqOD', hasEqualOriginAndDestination, 'air', trip.air_distance, 'dist', trip.distance)
		}
	})

	// Add the last journey
	if (currentJourney.trips.length > 0) {
		journeys.push(currentJourney)
	}

	return journeys
}

function mergeWalkingWithPublicTransport(journeys) {
	console.warn('mergeWalkingWithPublicTransport')
	if (journeys.length < 2) return journeys

	const WALKING_TO_TRANSIT_MAX_GAP = 1000 * 60 * 10 // 10 minutes

	const mergedJourneys = []
	let i = 0

	while (i < journeys.length) {
		const currentJourney = journeys[i]

		// Check if this is a single-trip walking journey
		const isSingleWalkingTrip = currentJourney.trips.length === 1 && currentJourney.trips[0].activity_name === 'walking'

		// Check if there's a next journey
		const hasNextJourney = i + 1 < journeys.length

		if (isSingleWalkingTrip && hasNextJourney) {
			const nextJourney = journeys[i + 1]

			// Check if next journey has public transport
			const hasPublicTransport = nextJourney.trips.some((trip) => isPublicTransport(trip))

			// Calculate time gap between walking trip and next journey's first trip
			const walkingTripArrival = currentJourney.trips[0].arrival_timestamp
			const nextJourneyStart = nextJourney.trips[0].departure_timestamp
			const timeGap = nextJourneyStart - walkingTripArrival

			console.warn('mergeWalkingWithPublicTransport', { currentJourney, nextJourney, timeGap })

			// Merge if conditions are met
			if (hasPublicTransport && timeGap < WALKING_TO_TRANSIT_MAX_GAP) {
				// Merge: prepend walking trip to next journey
				nextJourney.trips.unshift(currentJourney.trips[0])
				// Skip current journey (it's been merged), move to next
				i++
				continue
			}
		}

		// If not merging, add current journey to result
		mergedJourneys.push(currentJourney)
		i++
	}

	return mergedJourneys
}

function mergeJourneysByAerowayTerminal(journeys) {
	console.warn('mergeJourneysByAerowayTerminal')
	if (!Array.isArray(journeys) || journeys.length < 2) return journeys

	const mergedJourneys = []
	let currentJourney = journeys[0]

	for (let i = 1; i < journeys.length; i++) {
		const nextJourney = journeys[i]
		const lastTrip = currentJourney?.trips?.[currentJourney.trips.length - 1]
		const firstTrip = nextJourney?.trips?.[0]

		const endsAtAerowayTerminal = hasAerowayTerminalPoi(lastTrip?.destination_pois)
		const startsAtAerowayTerminal = hasAerowayTerminalPoi(firstTrip?.origin_pois)
		const lastDestinationTimestamp = Number(lastTrip?.destination?.timestamp)
		const nextOriginTimestamp = Number(firstTrip?.origin?.timestamp)
		const hasValidTimestamps = Number.isFinite(lastDestinationTimestamp) && Number.isFinite(nextOriginTimestamp)
		const gapMs = hasValidTimestamps ? nextOriginTimestamp - lastDestinationTimestamp : Number.POSITIVE_INFINITY
		const maxGapMs = 1000 * 60 * 60 * 2 // 2 hours
		const withinThreeHours = gapMs >= 0 && gapMs <= maxGapMs

		if (endsAtAerowayTerminal && startsAtAerowayTerminal && withinThreeHours) {
			currentJourney.trips = [...currentJourney.trips, ...nextJourney.trips]
			currentJourney.origin = currentJourney.trips[0]?.origin ?? currentJourney.origin
			currentJourney.destination = currentJourney.trips[currentJourney.trips.length - 1]?.destination ?? currentJourney.destination
		} else {
			mergedJourneys.push(currentJourney)
			currentJourney = nextJourney
		}
	}

	if (currentJourney) mergedJourneys.push(currentJourney)

	return mergedJourneys
}

function hasAerowayTerminalPoi(pois) {
	if (!Array.isArray(pois)) return false
	return pois.some((poi) => poi?.type === 'aeroway:terminal')
}

function assignJourneysToDays(journeys, surveyDefinitions) {
	const days = []

	journeys.forEach((journey, journeyIndex) => {
		// Assign journey to day based on the first trip's departure time
		const journeyStartTime = journey.trips[0].departure_timestamp
		const dayDate = getDayFromTimestamp(journeyStartTime)

		// Set journey properties
		journey.date = dayDate
		journey.index = journeyIndex
		journey.name = journeyIndex.toString()

		// Add journey to the appropriate day
		addJourneyToDays(days, journey)
	})

	// Add trip indexes and surveys
	addTripsIndexesAndSurveys(days, surveyDefinitions)

	return days
}

function addJourneyToDays(days, journey) {
	const dayEntry = days.find((d) => d.date === journey.date)
	if (!dayEntry) {
		days.push({
			date: journey.date,
			journeys: [journey],
			autofill: {},
		})
	} else {
		dayEntry.journeys.push(journey)
	}
}

function createNewJourney(trip, journeyIndex) {
	return {
		trips: [trip],
		index: journeyIndex,
		name: journeyIndex.toString(),
		origin: {},
		destination: {},
	}
}

// Check if trip activity_name is tram, bus, train, metro, or water
function isPublicTransport(trip) {
	return ['tram', 'bus', 'train', 'metro', 'water'].includes(trip.activity_name)
}

/**
 * Check if two consecutive walking trips should merge based on POI matching
 * @param {Object} a - The first walking trip
 * @param {Object} b - The second walking trip
 * @returns {boolean} - True if trips should merge (no POI match), false otherwise
 */
function walkingTripsShouldMergeByPOI(a, b) {
	const aDest = Array.isArray(a?.destination_pois) ? a.destination_pois : null
	const bOrig = Array.isArray(b?.origin_pois) ? b.origin_pois : null

	// If either side has no POIs, merge
	if (!aDest || aDest.length === 0) return true
	if (!bOrig || bOrig.length === 0) return true

	// If both have POIs but none share the same id, merge
	return !haveMatchingPOIIds(aDest, bOrig)
}

/**
 * Check if two POI lists have any matching IDs
 * @param {Array} listA - First list of POIs
 * @param {Array} listB - Second list of POIs
 * @returns {boolean} - True if there's a matching POI ID, false otherwise
 */
function haveMatchingPOIIds(listA, listB) {
	const idsA = new Set(listA.map((p) => p?.id).filter(Boolean))
	for (const p of listB) {
		const id = p?.id
		if (id && idsA.has(id)) return true
	}
	return false
}

function addTripsIndexesAndSurveys(days, surveyDefinitions) {
	for (var d = 0; d < days.length; d++) {
		const day = days[d]
		for (var j = 0; j < day.journeys.length; j++) {
			const journey = day.journeys[j]
			for (var t = 0; t < journey.trips.length; t++) {
				const trip = journey.trips[t]
				trip.index_relative = t
				trip.index_journey = journey.index
				trip.index_day = d
			}
			if (surveyDefinitions) {
				addSurveyToJourney(journey, journey.index, surveyDefinitions)
			}
		}
	}
}

// export function getDayFromTimestamp(timestamp) {
// 	const date = new Date(timestamp)
// 	// Subtract 4 hours
// 	date.setHours(date.getHours() - 4)
// 	// Return the date in YYYY-MM-DD format
// 	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
// }

// export function getDaysWithJourneys(trips, surveyDefinitions) {
// 	console.warn('getDaysWithJourneys', JSON.parse(JSON.stringify(trips)))

// 	if (trips.length === 0) return []

// 	trips.sort((a, b) => a.departure_timestamp - b.departure_timestamp)

// 	if (trips.length) {
// 		trips.forEach((trip, index) => {
// 			if (trip) {
// 				trip.index_absolute = index
// 			}
// 		})
// 	}

// 	const days = []
// 	let journey = {
// 		index: 0,
// 		date: '',
// 		trips: [],
// 		name: '0',
// 		origin: {},
// 		destination: {},
// 	}

// 	// Define duration constants in milliseconds
// 	const DEFAULT_MAX_DURATION = 1000 * 60 * 2 // 2 minutes
// 	const PUBLIC_TRANSIT_MAX_DURATION = 1000 * 60 * 30 // 30 minutes
// 	const AIRTRAVEL_MAX_DURATION = 1000 * 60 * 60 * 3 // 3 hours

// 	trips.forEach((trip, index) => {
// 		// Skip if trip is null or undefined
// 		if (trip == null) return

// 		if (index === 0) {
// 			journey.trips.push(trip)
// 			journey.date = getDayFromTimestamp(trip.departure_timestamp)
// 			return
// 		}

// 		const lastTrip = journey.trips[journey.trips.length - 1]
// 		const timeDifference = trip.departure_timestamp - lastTrip.arrival_timestamp

// 		// Determine max duration based on next trip type
// 		let maxDuration = DEFAULT_MAX_DURATION
// 		if (trip.activity_name === 'airtravel') {
// 			maxDuration = AIRTRAVEL_MAX_DURATION
// 		} else if (isPublicTransport(trip)) {
// 			maxDuration = PUBLIC_TRANSIT_MAX_DURATION
// 		}

// 		const prevArrivalHour = new Date(lastTrip.arrival_timestamp).getHours()
// 		const currentDepartureHour = new Date(trip.departure_timestamp).getHours()
// 		const isWithinMaxDuration = timeDifference < maxDuration
// 		const isCrossingBoundary = prevArrivalHour < 4 && currentDepartureHour >= 4
// 		const isSameDay = getDayFromTimestamp(lastTrip.departure_timestamp) === getDayFromTimestamp(trip.departure_timestamp)
// 		const isSplitted = lastTrip.turn_split && trip.turn_split && lastTrip.turn_index == trip.turn_index

// 		// Trips that are ABA should in general be a separate journey
// 		const hasEqualOriginAndDestination = trip.air_distance < 100 && trip.distance > 200

// 		if (isWithinMaxDuration && !isCrossingBoundary && isSameDay && !isSplitted && !hasEqualOriginAndDestination) {
// 			journey.trips.push(trip)
// 		} else {
// 			addJourneyToDays(days, journey)
// 			journey = createNewJourney(trip, journey.index)
// 		}
// 	})

// 	addJourneyToDays(days, journey)

// 	// Add trip indexes, mostly for debugging
// 	for (var d = 0; d < days.length; d++) {
// 		const day = days[d]
// 		for (var j = 0; j < day.journeys.length; j++) {
// 			const journey = day.journeys[j]
// 			for (var t = 0; t < journey.trips.length; t++) {
// 				const trip = journey.trips[t]
// 				trip.index_relative = t
// 				trip.index_absolute = trip.index_absolute
// 				trip.index_journey = journey.index
// 				trip.index_day = d
// 				if (surveyDefinitions) {
// 					addSurveyToJourney(journey, journey.index, surveyDefinitions)
// 				}
// 			}
// 		}
// 	}

// 	return days
// }

// // Check if trip activity_name is tram, bus, train, metro, or water
// function isPublicTransport(trip) {
// 	return ['tram', 'bus', 'train', 'metro', 'water'].includes(trip.activity_name)
// }

// function addJourneyToDays(days, journey) {
// 	const day = getDayFromTimestamp(journey.trips[0].departure_timestamp)
// 	const dayEntry = days.find((d) => d.date === day)
// 	if (!dayEntry) {
// 		days.push({
// 			date: day,
// 			journeys: [journey],
// 			autofill: {},
// 		})
// 	} else {
// 		dayEntry.journeys.push(journey)
// 	}
// }
//
// function createNewJourney(trip, journeyIndex) {
// 	return {
// 		trips: [trip],
// 		date: trip.departure_timestamp,
// 		index: journeyIndex + 1,
// 		name: (journeyIndex + 1).toString(),
// 	}
// }

/**
 * Recursively processes survey objects and replaces placeholder IDs
 * @param {Object} obj - The survey object to process
 * @param {number|string} journeyIndex - The journey index to insert
 * @param {number|string} tripIndex - The trip index to insert
 */
function replaceIds(obj, journeyIndex, tripIndex) {
	// Base case: not an object or null
	if (!obj || typeof obj !== 'object') return

	// Replace ID if it exists
	if (obj.id && typeof obj.id === 'string') {
		obj.id = obj.id.replace('{journeyIndex}', journeyIndex + 1).replace('{tripIndex}', tripIndex + 1)
	}

	// Initialize answer field if this is a survey question object
	if ('options' in obj || 'sub' in obj) {
		obj.answer = ''
	}

	// Process options array if it exists
	if (Array.isArray(obj.options)) {
		obj.options.forEach((option) => replaceIds(option, journeyIndex, tripIndex))
	}

	// Process sub property if it exists
	if (obj.sub) {
		replaceIds(obj.sub, journeyIndex, tripIndex)
	}
}

/**
 * Maps survey places to journey trips based on proximity
 * @param {Object} journey - The journey object
 * @param {Array} surveyPlaces - Array of survey places
 */
function mapSurveyPlacesToJourneyTrips(journey, surveyDefinitions) {
	if (surveyDefinitions.variables && surveyDefinitions.variables.places) {
		const places = surveyDefinitions.variables.places

		// Expand surveyDefinitions.variables.places with title and subTitle based on address
		places.forEach((place) => {
			var addressParts = place.address.split(',')
			place.title = addressParts[0].trim()
			place.subTitle = addressParts.length > 1 ? addressParts[1].trim() : ''

			// If places from survey definitions does not have place_id, set is based on id
			if (place.place_id == null) place.place_id = place.id
		})

		if (!journey || !journey.trips || !places || places.length === 0) {
			return
		}

		function copyPlaceData(toItem, fromItem) {
			if (fromItem.place_id) toItem.place_id = fromItem.place_id
			if (fromItem.place_distance) toItem.place_distance = fromItem.place_distance
			if (fromItem.place) toItem.place = fromItem.place
			if (fromItem.title) toItem.title = fromItem.title
			if (fromItem.subTitle) toItem.subTitle = fromItem.subTitle
			if (fromItem.place_id) toItem.place_id = fromItem.place_id
			if (fromItem.type) toItem.type = fromItem.type
			toItem.from_home = fromItem.from_home || false
			toItem.from_work = fromItem.from_work || false
			toItem.to_home = fromItem.to_home || false
			toItem.to_work = fromItem.to_work || false
		}

		journey.trips.forEach((trip, index) => {
			// Reset home/work
			trip.from_home = false
			trip.from_work = false
			trip.to_home = false
			trip.to_work = false
			trip.origin.from_home = false
			trip.origin.from_work = false
			trip.destination.to_home = false
			trip.destination.to_work = false

			// Check if we have a place from background variables that is less than 250 meters from the origin
			const originPlace = places.find((place) => {
				const distance = calculateLocationDistance(place, trip.origin)
				return distance < 200
			})

			// Check if we have a place from background variables that is less than 250 meters from the destination
			const destinationPlace = places.find((place) => calculateLocationDistance(place, trip.destination) < 200)

			if (originPlace) {
				// Copy place data to trip origin
				copyPlaceData(trip.origin, originPlace)
				trip.origin.place_distance = calculateLocationDistance(trip.origin, originPlace)

				if (trip.origin.type === 'home') {
					trip.origin.from_home = true
					trip.from_home = true
				}
				if (trip.origin.type === 'work') {
					trip.origin.from_work = true
					trip.from_work = true
				}
			}

			if (destinationPlace) {
				// Copy place data to trip destination
				copyPlaceData(trip.destination, destinationPlace)
				trip.destination.place_distance = calculateLocationDistance(trip.destination, destinationPlace)

				if (trip.destination.type === 'home' && trip.destination.place_distance < 100) {
					trip.destination.to_home = true
					trip.to_home = true
				}
				if (trip.destination.type === 'work') {
					trip.destination.to_work = true
					trip.to_work = true
				}
			}
		})

		// Journey origin and destination will be copied later as well, but we can add them here as well, just to make sure they are set
		copyPlaceData(journey.origin, journey.trips[0].origin)
		copyPlaceData(journey.destination, journey.trips[journey.trips.length - 1].destination)
	}
}

export function clearSurveyAnswers(survey) {
	// Validate input
	if (!survey || !survey.options) {
		return
	}

	// Clear each option's selected state
	survey.options.forEach((option) => {
		option.selected = false
		// Recursively clear sub-surveys if they exist
		if (option.sub) {
			clearSurveyAnswers(option.sub)
		}
	})

	// Remove answer-related properties
	delete survey.answer
	delete survey.answerCode
	delete survey.answerValue

	console.log('clearSurveyAnswers completed for survey', survey)
}

export function autoCompleteSurveyForJourney(survey, autofillData) {
	const isAnswered = survey?.answer && typeof survey.answer === 'object'
	console.warn('autoCompleteSurveyForJourney', survey, autofillData, survey?.answer, typeof survey.answer === 'object', isAnswered)

	// Early return if required data is missing or if question is already answered
	if (!survey || !autofillData || isAnswered) {
		console.warn('autoCompleteSurveyForJourney invalid', !survey, !autofillData, 'A', isAnswered, 'B')
		return
	}

	function clearOptions(options) {
		if (!options || !Array.isArray(options)) return
		options.forEach((option) => {
			option.selected = false
			option.answer = null
			if (option.sub) clearOptions(option.sub.options)
		})
	}

	// Clear earlier answers in options
	clearOptions(survey.options)
	var didAutofill = false

	applyAutofill({
		question: survey,
		autofillField: autofillData['FLOKK_REISE'],
		questionIdPrefix: 'FLOKK_REISE_R',
		onSuccess: () => {
			didAutofill = true
		},
	})
	applyAutofill({
		question: survey,
		autofillField: autofillData['FLOKK_ANTALL'],
		questionIdPrefix: 'FLOKK_ANTALL_R',
		onSuccess: () => {
			didAutofill = true
		},
	})
	applyAutofill({
		question: survey,
		autofillField: autofillData['FLOKK_BARN'],
		questionIdPrefix: 'FLOKK_BARN_R',
		onSuccess: () => {
			didAutofill = true
		},
	})

	return didAutofill
}

/**
 * Autocompletes survey questions for journey trips using provided autofill data
 */
export function autoCompleteSurveyForJourneyTrip(trip, autofillData) {
	const question = trip.survey
	const activityName = trip.activity_name
	const isAnswered = question?.answer && typeof question.answer === 'object'

	console.warn('autoCompleteSurveyForJourneyTrip', trip.index_relative, isAnswered, activityName, question, autofillData)

	// Early return if required data is missing or if question is already answered
	if (!question || !autofillData || !autofillData[activityName] || isAnswered) {
		console.warn('autoCompleteSurveyForJourneyTrip noata', trip, autofillData)
		return
	}

	// Handle transport mode question
	applyAutofill({
		question: question,
		autofillField: autofillData[activityName].TRANSPORTMIDDEL,
		questionIdPrefix: 'TRANSPORTMIDDEL_R',
		onSuccess: () => {
			// Handle car driver question if transport is car
			const subQuestion = question.answer?.sub
			if (subQuestion) {
				applyAutofill({
					question: subQuestion,
					autofillField: autofillData[activityName].BIL_PASSASJER_DRIVSTOFF,
					questionIdPrefix: 'BIL_PASSASJER_DRIVSTOFF_R',
				})
				applyAutofill({
					question: subQuestion,
					autofillField: autofillData[activityName].BIL_SJOFOR,
					questionIdPrefix: 'BIL_SJOFOR_R',
				})
				applyAutofill({
					question: subQuestion,
					autofillField: autofillData[activityName].LASTESYKKEL,
					questionIdPrefix: 'LASTESYKKEL_R',
				})
			}
		},
	})
}

/**
 * Helper function to apply autofill data to a question
 */
function applyAutofill({ question, autofillField, questionIdPrefix, onSuccess }) {
	if (!autofillField || !question.id.startsWith(questionIdPrefix)) {
		return
	}

	const answerCode = autofillField.code
	const option = question.options.find((option) => option.code === answerCode)

	if (option) {
		question.answer = option
		question.answerCode = answerCode
		console.warn(`Applied ${questionIdPrefix} autofill:`, option)
		if (onSuccess) onSuccess()
	}
}

export function autoCompleteSurveyForDestination(survey, destination, source) {
	if (!survey || !destination || !survey.options) {
		console.error('autoCompleteSurveyForDestination missing data', survey, destination, source)
		return
	}
	console.warn('autoCompleteSurveyForDestination started', survey, destination, 'source', source)

	function clearOptions(options) {
		if (!options || !Array.isArray(options)) return
		options.forEach((option) => {
			option.selected = false
			option.answer = null
			if (option.sub) clearOptions(option.sub.options)
		})
	}

	function setAnswer(survey, option) {
		console.warn('setAnswer', survey.id, option, 'for destination', destination.name, 'source', survey)
		option.selected = true
		survey.answer = option
		if (option.code) survey.answerCode = option.code
		if (option.value) survey.answerValue = option.value
		if (option.sub) autoCompleteSurveyForDestination(option.sub, destination, 'recursive')
	}

	// Clear previous answers
	clearOptions(survey.options)

	for (const option of survey.options) {
		// Try to auto select based on place type, e.g. home, work, etc.
		if (option.place_auto_select) {
			if (destination.type == option.place_auto_select) {
				console.warn('autoSelected', option.name, 'beacuse of place type', option.place_auto_select, 'for survey', survey)
				setAnswer(survey, option)
				break
			}
		}

		// Try to auto select based on specific location (latitude/longitude)
		if (option.location_auto_select) {
			const locationLat = Number(destination.latitude)
			const locationLng = Number(destination.longitude)
			const optionLat = Number(option.latitude)
			const optionLng = Number(option.longitude)
			if (Number.isFinite(locationLat) && Number.isFinite(locationLng) && Number.isFinite(optionLat) && Number.isFinite(optionLng)) {
				const distance = calculateLocationDistance({ latitude: locationLat, longitude: locationLng }, { latitude: optionLat, longitude: optionLng })
				if (distance < 2000) {
					console.warn('autoSelected', option.name, 'because of location match', distance, 'meters', 'for survey', survey)
					setAnswer(survey, option)
					break
				}
			}
		}

		// Try to auto select based on categories
		if (destination.categories && destination.categories.length > 0) {
			if (option.category_auto_select && option.category_auto_select.length > 0) {
				// Check if destination.categories contains any of the auto select categories
				const foundCategory = destination.categories.find((category) => option.category_auto_select.includes(category))
				const foundType = destination.placeTypes.find((type) => option.category_auto_select.includes(type))
				if (foundCategory || foundType) {
					console.warn('autoSelected', option.name, 'beacuse of category', foundCategory, 'or type', foundType, 'for survey', survey)
					setAnswer(survey, option)
					break
				}
			}
		}
	}

	// Autoselect if destination has surveyResponses
	if (destination.surveyResponses) {
		console.warn('autoCompleteSurveyForDestination surveyResponses', survey, destination.surveyResponses)
		for (const questionId of Object.keys(destination.surveyResponses)) {
			let code = destination.surveyResponses[questionId]
			let baseId = questionId.replace(/_R\d+.*$/, '')
			let surveyBaseId = survey.id.replace(/_R\d+.*$/, '')
			if (baseId == surveyBaseId) {
				let option = survey.options.find((opt) => opt.code == code)
				// Check if we found a matching option
				if (option) {
					console.warn('autoCompleteSurveyForDestination match for surveyResponses', questionId, surveyBaseId, code, option)
					setAnswer(survey, option)
					break
				}
			}
		}
	}
}

/**
 * Adds survey definitions to a journey and its trips
 * @param {Object} journey - The journey object
 * @param {number|string} journeyIndex - Index of the journey
 * @param {Object} surveyDefinitions - Survey definitions object
 */
export function addSurveyToJourney(journey, journeyIndex, surveyDefinitions) {
	console.warn('addSurveyToJourney', journeyIndex)
	if (!journey || !surveyDefinitions || !surveyDefinitions.journey || !surveyDefinitions.trip) {
		return
	}
	try {
		// Get trip survey definition by cloning to avoid modifying the original
		const surveyTripDefinition = cloneDeep(surveyDefinitions.trip[0])

		// Map survey places to journey and journey trips (updates from_home, to_home, from_work, to_work etc)
		mapSurveyPlacesToJourneyTrips(journey, surveyDefinitions)

		const questionsForJourney = cloneDeep(surveyDefinitions.journey).filter((question) => {
			if (question.criteria && question.criteria.includes('travel_day_does_not_start_at_home')) {
				return !journey.trips[0].from_home && journeyIndex == 0
			}
			return true
		})

		questionsForJourney.forEach((question) => {
			replaceIds(question, journeyIndex)
		})
		journey.survey = questionsForJourney

		// Add origin and destination to journey
		journey.origin = journey.trips[0].origin
		journey.destination = journey.trips[journey.trips.length - 1].destination

		// Initialize survey completion flag
		journey.surveyTripsCompleted = false
		journey.hasTripSurveys = false

		if (!surveyTripDefinition.options || surveyTripDefinition.options.length === 0) {
			return // Nothing to process
		}

		// Process each trip in the journey
		journey.trips.forEach((trip, tripIndex) => {
			// Create a deep copy of the survey definition for this trip
			const tripSurvey = cloneDeep(surveyTripDefinition)

			// Filter options based on activity criteria
			const optionsForTrip = tripSurvey.options.filter((option) => !option.activity_criteria || option.activity_criteria.includes(trip.activity_name))

			// Skip if no applicable options
			if (optionsForTrip.length < 1 || trip.deleted) {
				// Remove the survey from the trip in case it had one before
				delete trip.survey
				return // No applicable options for this trip
			}

			// Replace the options with the filtered set
			tripSurvey.options = optionsForTrip

			// Initialize survey completion flag
			tripSurvey.autocompleted = tripSurvey.options.length < 2 ? true : false
			tripSurvey.completed = tripSurvey.autocompleted

			// Process all IDs in the survey object and its nested properties
			replaceIds(tripSurvey, journeyIndex, tripIndex)

			// Autocomplete when only one option is available
			if (tripSurvey.options.length === 1) {
				tripSurvey.options[0].selected = true
				tripSurvey.answer = tripSurvey.options[0]
			}

			// Dont add survey to deleted trips
			if (!trip.deleted) {
				// Assign the processed survey to the trip
				trip.survey = tripSurvey
				// Mark the journey as having surveys
				journey.hasTripSurveys = true
			}
		})
	} catch (error) {
		console.error('Error in addSurveyToJourney:', error)
	}
}

/**
 * Adds survey definitions to a journey day and its trips
 * @param {Object} journeyDay - The journey day object
 * @param {Object} surveyDefinitions - Survey definitions object
 */
export function addSurveyToJourneyDay(journeyDay, surveyDefinitions) {
	console.warn('addSurveyToJourneyDay', journeyDay)
	if (surveyDefinitions && surveyDefinitions.post) {
		const postSurvey = cloneDeep(surveyDefinitions.post)
		const filteredSurvey = postSurvey.filter((question) => {
			if (question.id == 'ALTERNATIV_BIL' && !journeyDay.journeys.some((journey) => journey.trips.some((trip) => !trip.deleted && trip.activity_name == 'automotive'))) return false
			return true
		})
		journeyDay.survey = filteredSurvey
	}
}

/**
 * Extracts survey responses as key/value pairs
 * @param {Object} surveyObj The survey object to extract responses from
 * @param {Object} result The result object to populate (passed by reference)
 * @return {Object} Object with question IDs as keys and response values as values
 */
export function extractResponsesFromSurvey(surveyObj, result = {}) {
	// Validate input
	if (!surveyObj || typeof surveyObj !== 'object') return result

	// Process the survey object if it has an ID and an answer
	if (surveyObj.id && surveyObj.answer) {
		if (Array.isArray(surveyObj.answer)) {
			// Multiple-choice: answer is an array of selected options
			const selectedValues = []
			surveyObj.answer.forEach((item) => {
				if (item.code !== undefined) {
					selectedValues.push(item.code)
				} else if (item.value !== undefined) {
					selectedValues.push(item.value)
				}
				// Recursively process sub-surveys in each selected option
				if (item.sub) {
					extractResponsesFromSurvey(item.sub, result)
				}
			})
			result[surveyObj.id] = selectedValues
		} else if (typeof surveyObj.answer === 'object' && surveyObj.answer !== null) {
			// Single-choice: answer is an object
			if (surveyObj.answer.code !== undefined) {
				result[surveyObj.id] = surveyObj.answer.code
			} else if (surveyObj.answer.value !== undefined) {
				result[surveyObj.id] = surveyObj.answer.value
			}
			// Recursively process sub-surveys in the answer object
			if (surveyObj.answer.sub) {
				extractResponsesFromSurvey(surveyObj.answer.sub, result)
			}
		} else if (typeof surveyObj.answer === 'string' && surveyObj.answer.trim() !== '') {
			// Direct string answer
			result[surveyObj.id] = surveyObj.answer
		}
	}

	// Recursively process sub property if it exists
	if (surveyObj.sub) {
		extractResponsesFromSurvey(surveyObj.sub, result)
	}

	return result
}

/**
 * Extracts all survey responses from a journey
 * @param {Object} journey The journey object containing survey responses
 * @return {Object} Object with question IDs as keys and response values as values
 */
export function extractJourneySurveyResponses(journey) {
	const results = {}

	if (!journey || !journey.trips) return results

	// Extract survey responses from all trips
	journey.trips.forEach((trip) => {
		if (trip.survey) {
			extractResponsesFromSurvey(trip.survey, results)
		}
	})

	// Extract journey survey responses if they exist
	if (journey.survey) {
		if (Array.isArray(journey.survey)) {
			journey.survey.forEach((survey) => {
				extractResponsesFromSurvey(survey, results)
			})
		} else {
			extractResponsesFromSurvey(journey.survey, results)
		}
	}

	return results
}

export function getGuid() {
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
		var r = (Math.random() * 16) | 0,
			v = c == 'x' ? r : (r & 0x3) | 0x8
		return v.toString(16)
	})
}

export function evaluateSurveyResponses(item, type) {
	//console.warn('evaluateSurveyResponses', type, item)
	if (!item || !item.survey) {
		console.warn('evaluateSurveyResponses no survey', item)
		return false // No survey to evaluate
	}
	/**
	 * Recursively checks if all required questions in a survey object have answers
	 * @param {Object} surveyObj - The survey object to check
	 * @returns {boolean} - True if all required questions have answers, false otherwise
	 */
	function checkSurveyCompletion(surveyObj) {
		if (!surveyObj || typeof surveyObj !== 'object') {
			return true // Base case: nothing to check
		}
		// If this is a question with options, it must have a valid answer
		if (Array.isArray(surveyObj.options)) {
			// Check if there's a valid answer (object with code or selected option)
			var hasAnswer = surveyObj.answer && typeof surveyObj.answer === 'object'
			if (hasAnswer) {
				// Check if answer is a object or array of objects (multiple choice)
				if (Array.isArray(surveyObj.answer)) {
					// Check if answer has an item with a code or selected option
					hasAnswer = surveyObj.answer.length > 0 && surveyObj.answer.some((ans) => ans.code !== undefined || ans.selected)
				} else {
					// Check if answer has a code or selected option
					hasAnswer = surveyObj.answer.code !== undefined || surveyObj.answer.selected
				}
			}
			if (!hasAnswer) {
				return false
			}
			// Find the selected option
			const selectedOption = surveyObj.options.find((opt) => opt.selected || (surveyObj.answer && opt.code === surveyObj.answer.code))
			// If no option is selected when there should be, it's incomplete
			if (!selectedOption) {
				return false
			}
			// If the selected option has a sub, check it recursively
			if (selectedOption.sub) {
				// If sub has only one option, treat as automatically answered
				if (Array.isArray(selectedOption.sub.options) && selectedOption.sub.options.length === 1) {
					return true // Single option sub is considered answered
				}
				return checkSurveyCompletion(selectedOption.sub)
			}
			// If the answer object itself has a sub (nested in answer), check it
			if (surveyObj.answer.sub) {
				// If sub has only one option, treat as automatically answered
				if (Array.isArray(surveyObj.answer.sub.options) && surveyObj.answer.sub.options.length === 1) {
					return true // Single option sub is considered answered
				}
				return checkSurveyCompletion(surveyObj.answer.sub)
			}
		}
		// Check direct sub property if it exists and is relevant
		if (surveyObj.sub) {
			// If sub has only one option, treat as automatically answered
			if (Array.isArray(surveyObj.sub.options) && surveyObj.sub.options.length === 1) {
				return true // Single option sub is considered answered
			}
			return checkSurveyCompletion(surveyObj.sub)
		}
		return true
	}
	if (Array.isArray(item.survey)) {
		var allCompleted = true
		item.survey.forEach((survey) => {
			const surveyCompleted = checkSurveyCompletion(survey)
			// Set the completed flag
			survey.completed = surveyCompleted
			if (!surveyCompleted) allCompleted = false
		})
		return allCompleted
	} else {
		// Evaluate the survey completion
		const allAnswered = checkSurveyCompletion(item.survey)
		// Set the completed flag
		item.survey.completed = allAnswered
		return allAnswered
	}
}

/**
 * Evaluates survey completion for all trips in a journey
 * @param {Object} journey - The journey object containing trips
 * @returns {boolean} - True if all trip surveys are complete, false otherwise
 */
export function evaluateJourneySurveyResponses(journey) {
	if (!journey || !journey.trips) {
		return false
	}

	let allTripsComplete = true
	let allTripsAutocompleted = true

	journey.trips.forEach((trip) => {
		if (trip.survey && !evaluateSurveyResponses(trip, 'trip')) {
			allTripsComplete = false
		}
		if (!trip.survey?.autocompleted) {
			allTripsAutocompleted = false
		}
	})

	journey.surveyTripsCompleted = allTripsComplete
	journey.surveyTripsAutocompleted = allTripsAutocompleted
	journey.surveyJourneyCompleted = evaluateSurveyResponses(journey, 'journey')

	return allTripsComplete
}

/**
 * Evaluates survey completion for all journeys in a day
 * @param {Object} journeyDay - The journey day object containing journeys
 * @returns {boolean} - True if all journey surveys are complete, false otherwise
 */
export function evaluateJourneyDaySurveyResponses(journeyDay) {
	if (!journeyDay || !journeyDay.journeys) {
		return false
	}

	let allJourneysComplete = true

	// Check all journeys in the day, which also checks all trips in each journey
	journeyDay.journeys.forEach((journey) => {
		if (journey.survey && !evaluateJourneySurveyResponses(journey)) {
			allJourneysComplete = false
		}
	})

	// Set the completed flag for journeys
	journeyDay.surveyJourneysCompleted = allJourneysComplete

	// Finally also check the journeyDay survey if we have any
	if (journeyDay.survey) {
		let allJourneyDaySurveysComplete = true
		journeyDay.survey.forEach((survey) => {
			if (!evaluateSurveyResponses({ survey }, 'day')) {
				allJourneyDaySurveysComplete = false
			}
		})
		// Set the completed flag for the day
		journeyDay.surveyDayCompleted = allJourneyDaySurveysComplete
	} else {
		// journeyDay has no surveys
		journeyDay.surveyDayCompleted = false
		journeyDay.surveyDayNoSurveys = true
	}

	return journeyDay.surveyDayCompleted && journeyDay.surveyJourneysCompleted
}

/**
 * Removes questions with only one option from a survey objects
 * @param {Object} surveyDefinitions - The survey object to process
 */
export function filterSurveyDefinitions(surveyDefinitions) {
	if (surveyDefinitions) {
		if (surveyDefinitions.trip && surveyDefinitions.trip.length > 0) filterSurveyQuestion(surveyDefinitions.trip[0])
		if (surveyDefinitions.journey && surveyDefinitions.journey.length > 0) {
			surveyDefinitions.journey.forEach((question) => {
				filterSurveyQuestion(question)
			})
		}
		if (surveyDefinitions.post && surveyDefinitions.post.length > 0) {
			surveyDefinitions.post.forEach((question) => {
				filterSurveyQuestion(question)
			})
		}
	}
}

function filterSurveyQuestion(question) {
	if (!question || !question.options) return
	question.options.forEach((option) => {
		if (option.sub) {
			filterSurveyQuestion(option.sub)
			if (option.sub.options.length === 1) {
				delete option.sub
			}
		}
	})
}

export function isValidDate(date) {
	return date instanceof Date && !isNaN(date.getTime())
}

export function calculateDistance(lat1, lon1, lat2, lon2) {
	var p = 0.017453292519943295 // Math.PI / 180
	var c = Math.cos
	var a = 0.5 - c((lat2 - lat1) * p) / 2 + (c(lat1 * p) * c(lat2 * p) * (1 - c((lon2 - lon1) * p))) / 2
	return Math.round(12742 * 10000 * Math.asin(Math.sqrt(a))) / 10 // 2 * R; R = 6371 km
}

export function calculateLocationDistance(startLocation, endLocation) {
	return calculateDistance(startLocation.latitude, startLocation.longitude, endLocation.latitude, endLocation.longitude)
}

export function calculateDistances(locations) {
	if (locations.length > 0) {
		var startLocation = locations[0]
		var endLocation = locations[locations.length - 1]

		var max_location = {}
		var max_distance = 0
		var total_distance = 0
		var air_distance = calculateLocationDistance(startLocation, endLocation)

		for (var j = 0; j < locations.length; j++) {
			var distanceFromStart = calculateLocationDistance(startLocation, locations[j])
			if (distanceFromStart > max_distance) {
				max_distance = distanceFromStart
				max_location = locations[j]
			}
			if (j < locations.length - 1) {
				var distanceBetweenLocations = calculateLocationDistance(locations[j], locations[j + 1])
				locations[j].distanceFromStart = distanceFromStart
				total_distance += distanceBetweenLocations
			}
		}
		return {
			total_distance,
			max_distance,
			air_distance,
			max_location,
		}
	}
}

export function getIconForLocation(item) {
	// Most specific to least specific categories, when multiple categories are present and not mapping in item cateogry order
	const categoryIcons = {
		ski_area: 'fa-person-skiing',
		golf_course: 'fa-golf-ball-tee',
		airport: 'fa-plane',
		hospital: 'fa-hospital',
		dentist: 'fa-tooth',
		gym: 'fa-person-running',
		bar: 'fa-wine-glass',
		office: 'fa-building',
		daycare: 'fa-child',
		lodging: 'fa-bed',
		pizza_restaurant: 'fa-pizza-slice',
		burger_restaurant: 'fa-burger',
		restaurant: 'fa-utensils',
		food: 'fa-utensils',
		food_store: 'fa-utensils',
		school: 'fa-school',
		railway_station: 'fa-train',
		subway_station: 'fa-train-subway-tunnel',
		transit_station: 'fa-bus-simple',
		bus_station: 'fa-bus-simple',
		grocery: 'fa-cart-shopping',
		grocery_store: 'fa-cart-shopping',
		shopping_mall: 'fa-bag-shopping',
		shopping: 'fa-shop',
		event_space: 'fa-building',
		health_services: 'fa-briefcase-medical',
		farm: 'fa-tractor',
		parking_lot: 'fa-square-parking',
		mountain: 'fa-mountain',
		store: 'fa-store',
		pharmacy: 'fa-house-medical',
		cinema: 'fa-film',
	}
	if (item.categories && item.categories.length > 0) {
		// Iterate through each category in the item's categories array in order
		for (const category of item.categories) {
			// If this category has a matching icon, return it
			if (categoryIcons[category]) {
				return categoryIcons[category]
			}
		}
	}
	switch (item.type) {
		case 'work':
			return 'fa-building'
		case 'home':
			return 'fa-home'
		case 'poi':
			return 'fa-location-dot'
		case 'address':
			return 'fa-house'
		case 'place':
			return 'fa-location-dot'
		case 'train':
			return 'fa-train'
		case 'subway':
			return 'fa-train-subway-tunnel'
		case 'station':
			return 'fa-bus-simple'
		default:
			return 'fa-location-dot'
	}
}

export const getCategoriesForPlace = (place) => {}

/**
 * Maps OpenStreetMap POI types to app categories
 * @param {Object} poi - OSM POI object
 * @returns {Array<string>} Array of categories
 */
export const getCategoriesForPoi = (poi) => {
	if (!poi || typeof poi !== 'object') return []

	const categories = new Set()
	const add = (value) => {
		if (value) categories.add(value)
	}
	const addAll = (values) => {
		if (!values) return
		values.forEach((value) => add(value))
	}

	const type = (poi.type || '').toLowerCase()
	const tags = poi.tags || {}

	const typeCategoryMap = {
		'aeroway:aerodrome': ['airport'],
		'aeroway:terminal': ['airport'],
		'aeroway:gate': ['airport'],
		'amenity:hospital': ['hospital', 'health_services'],
		'amenity:clinic': ['health_services'],
		'amenity:doctors': ['health_services'],
		'amenity:dentist': ['dentist', 'health_services'],
		'amenity:pharmacy': ['pharmacy', 'health_services'],
		'amenity:bar': ['bar'],
		'amenity:pub': ['bar'],
		'amenity:restaurant': ['restaurant'],
		'amenity:fast_food': ['food'],
		'amenity:cafe': ['food'],
		'amenity:school': ['school'],
		'amenity:kindergarten': ['daycare'],
		'amenity:childcare': ['daycare'],
		'amenity:college': ['school'],
		'amenity:university': ['school'],
		'amenity:bus_station': ['bus_station', 'transit_station'],
		'amenity:parking': ['parking_lot'],
		'amenity:theatre': ['event_space'],
		'amenity:cinema': ['cinema'],
		'shop:supermarket': ['grocery_store', 'grocery'],
		'shop:convenience': ['grocery_store'],
		'shop:mall': ['shopping_mall', 'shopping'],
		'shop:department_store': ['shopping'],
		'shop:general': ['store'],
		'shop:clothes': ['shopping'],
		'shop:bakery': ['food_store', 'food'],
		'shop:butcher': ['food_store', 'food'],
		'shop:greengrocer': ['food_store', 'food'],
		'shop:deli': ['food_store', 'food'],
		'shop:coffee': ['food_store', 'food'],
		'shop:chemist': ['pharmacy'],
		'tourism:hotel': ['lodging'],
		'tourism:hostel': ['lodging'],
		'tourism:motel': ['lodging'],
		'tourism:apartment': ['lodging'],
		'tourism:camp_site': ['lodging'],
		'leisure:fitness_centre': ['gym'],
		'leisure:sports_centre': ['gym'],
		'leisure:swimming_pool': ['gym'],
		'office:government': ['office'],
		'office:company': ['office'],
		'office:ngo': ['office'],
		'office:yes': ['office'],
		'railway:station': ['railway_station', 'transit_station'],
		'railway:tram_stop': ['transit_station'],
		'railway:subway_entrance': ['subway_station', 'transit_station'],
		'railway:halt': ['railway_station'],
		'public_transport:station': ['transit_station'],
		'public_transport:stop_position': ['transit_station'],
		'highway:bus_stop': ['bus_station', 'transit_station'],
		'natural:peak': ['mountain'],
		'natural:mountain': ['mountain'],
		'natural:volcano': ['mountain'],
		'landuse:farm': ['farm'],
		'landuse:farmland': ['farm'],
	}

	if (type && typeCategoryMap[type]) {
		addAll(typeCategoryMap[type])
	}

	const parseType = (key, value) => {
		const normalizedKey = (key || '').toLowerCase()
		const normalizedValue = (value || '').toLowerCase()
		if (!normalizedKey || !normalizedValue) return
		const fullType = `${normalizedKey}:${normalizedValue}`
		if (typeCategoryMap[fullType]) {
			addAll(typeCategoryMap[fullType])
			return
		}
		if (normalizedKey === 'office') add('office')
		if (normalizedKey === 'shop') add('store')
		if (normalizedKey === 'amenity' && normalizedValue === 'parking') add('parking_lot')
		if (normalizedKey === 'tourism' && ['hotel', 'hostel', 'motel', 'apartment', 'camp_site'].includes(normalizedValue)) add('lodging')
		if (normalizedKey === 'railway' && normalizedValue === 'station') addAll(['railway_station', 'transit_station'])
		if (normalizedKey === 'public_transport') add('transit_station')
		if (normalizedKey === 'highway' && normalizedValue === 'bus_stop') addAll(['bus_station', 'transit_station'])
		if (normalizedKey === 'natural' && ['peak', 'mountain', 'volcano'].includes(normalizedValue)) add('mountain')
		if (normalizedKey === 'landuse' && ['farm', 'farmland'].includes(normalizedValue)) add('farm')
	}

	if (type && type.includes(':')) {
		const [key, value] = type.split(':')
		parseType(key, value)
	}

	parseType('amenity', tags.amenity)
	parseType('shop', tags.shop)
	parseType('tourism', tags.tourism)
	parseType('leisure', tags.leisure)
	parseType('railway', tags.railway)
	parseType('public_transport', tags.public_transport)
	parseType('highway', tags.highway)
	parseType('aeroway', tags.aeroway)
	parseType('landuse', tags.landuse)
	parseType('natural', tags.natural)
	parseType('office', tags.office)
	parseType('building', tags.building)

	return Array.from(categories)
}

/**
 * Maps OpenStreetMap POI types to Google Places-like place types
 * @param {Object} poi - OSM POI object
 * @returns {Array<string>} Array of place types
 */
export const getPlaceTypeForPoi = (poi) => {
	if (!poi || typeof poi !== 'object') return []

	const types = new Set()
	const add = (value) => {
		if (value) types.add(value)
	}
	const addAll = (values) => {
		if (!values) return
		values.forEach((value) => add(value))
	}

	const type = (poi.type || '').toLowerCase()
	const tags = poi.tags || {}

	const typeMap = {
		'pt:nsr_stop': ['transit_station'],
		'amenity:restaurant': ['restaurant', 'food', 'establishment'],
		'amenity:cafe': ['cafe', 'food', 'establishment'],
		'amenity:fast_food': ['meal_takeaway', 'food', 'establishment'],
		'amenity:bar': ['bar', 'establishment'],
		'amenity:pub': ['bar', 'establishment'],
		'amenity:ice_cream': ['ice_cream_shop', 'food', 'establishment'],
		'amenity:school': ['school', 'establishment'],
		'amenity:kindergarten': ['school', 'establishment'],
		'amenity:college': ['university', 'establishment'],
		'amenity:university': ['university', 'establishment'],
		'amenity:hospital': ['hospital', 'establishment'],
		'amenity:clinic': ['doctor', 'establishment'],
		'amenity:doctors': ['doctor', 'establishment'],
		'amenity:dentist': ['dentist', 'establishment'],
		'amenity:pharmacy': ['pharmacy', 'establishment'],
		'amenity:parking': ['parking'],
		'amenity:bus_station': ['bus_station', 'transit_station', 'establishment'],
		'amenity:cinema': ['movie_theater', 'establishment'],
		'amenity:theatre': ['performing_arts_theater', 'establishment'],
		'shop:supermarket': ['supermarket', 'grocery_or_supermarket', 'store', 'establishment'],
		'shop:convenience': ['convenience_store', 'grocery_or_supermarket', 'store', 'establishment'],
		'shop:mall': ['shopping_mall', 'store', 'establishment'],
		'shop:department_store': ['department_store', 'store', 'establishment'],
		'shop:clothes': ['clothing_store', 'store', 'establishment'],
		'shop:bakery': ['bakery', 'food', 'establishment'],
		'shop:butcher': ['food', 'store', 'establishment'],
		'shop:greengrocer': ['food', 'store', 'establishment'],
		'shop:deli': ['food', 'store', 'establishment'],
		'shop:chemist': ['pharmacy', 'store', 'establishment'],
		'shop:general': ['store', 'establishment'],
		'tourism:hotel': ['lodging', 'establishment'],
		'tourism:hostel': ['lodging', 'establishment'],
		'tourism:motel': ['lodging', 'establishment'],
		'tourism:apartment': ['lodging', 'establishment'],
		'tourism:camp_site': ['lodging', 'establishment'],
		'leisure:park': ['park'],
		'leisure:fitness_centre': ['gym', 'health', 'establishment'],
		'leisure:sports_centre': ['gym', 'health', 'establishment'],
		'leisure:swimming_pool': ['gym', 'health', 'establishment'],
		'railway:station': ['train_station', 'transit_station', 'establishment'],
		'railway:tram_stop': ['transit_station', 'establishment'],
		'railway:subway_entrance': ['subway_station', 'transit_station', 'establishment'],
		'highway:bus_stop': ['bus_station', 'transit_station', 'establishment'],
		'aeroway:aerodrome': ['airport', 'establishment'],
		'aeroway:terminal': ['airport', 'establishment'],
	}

	if (type && typeMap[type]) {
		addAll(typeMap[type])
	}

	const parseType = (key, value) => {
		const normalizedKey = (key || '').toLowerCase()
		const normalizedValue = (value || '').toLowerCase()
		if (!normalizedKey || !normalizedValue) return
		const fullType = `${normalizedKey}:${normalizedValue}`
		if (typeMap[fullType]) {
			addAll(typeMap[fullType])
			return
		}
		if (normalizedKey === 'shop') addAll(['store', 'establishment'])
		if (normalizedKey === 'amenity') add('establishment')
		if (normalizedKey === 'tourism') add('establishment')
		if (normalizedKey === 'office') addAll(['establishment'])
		if (normalizedKey === 'building') addAll(['establishment'])
	}

	if (type && type.includes(':')) {
		const [key, value] = type.split(':')
		parseType(key, value)
	}

	parseType('amenity', tags.amenity)
	parseType('shop', tags.shop)
	parseType('tourism', tags.tourism)
	parseType('leisure', tags.leisure)
	parseType('railway', tags.railway)
	parseType('public_transport', tags.public_transport)
	parseType('highway', tags.highway)
	parseType('aeroway', tags.aeroway)
	parseType('office', tags.office)
	parseType('building', tags.building)

	if (types.size === 0) add('establishment')

	return Array.from(types)
}

/**
 * Maps OpenStreetMap POI types to Norwegian subtitles
 * @param {Object} poi - OSM POI object
 * @returns {string} Norwegian subtitle
 */
export const getSubtitleForPoi = (poi) => {
	if (!poi || typeof poi !== 'object') return ''

	const type = (poi.type || '').toLowerCase()

	const poiTypeToNorwegian = {
		// --- Amenities ---
		'amenity:bar': 'Bar',
		'amenity:cafe': 'Kafé',
		'amenity:car_rental': 'Bilutleie',
		'amenity:charging_station': 'Ladestasjon',
		'amenity:cinema': 'Kino',
		'amenity:clinic': 'Klinikk',
		'amenity:college': 'Høyskole',
		'amenity:courthouse': 'Tingrett',
		'amenity:dentist': 'Tannlege',
		'amenity:doctors': 'Legekontor',
		'amenity:fast_food': 'Hurtigmat',
		'amenity:food_court': 'Matservering',
		'amenity:fuel': 'Bensinstasjon',
		'amenity:ice_cream': 'Iskrembar',
		'amenity:kindergarten': 'Barnehage',
		'amenity:library': 'Bibliotek',
		'amenity:place_of_worship': 'Gudshus',
		'amenity:post_office': 'Postkontor',
		'amenity:pub': 'Pub',
		'amenity:restaurant': 'Restaurant',
		'amenity:school': 'Skole',
		'amenity:theatre': 'Teater',
		'amenity:townhall': 'Rådhus',
		'amenity:university': 'Universitet',
		'amenity:veterinary': 'Veterinær',

		// --- Buildings ---
		'building:hotel': 'Hotellbygning',
		'building:retail': 'Butikklokale',
		'building:school': 'Skolebygning',
		'building:train_station': 'Togstasjon',
		'building:university': 'Universitetsbygning',

		// --- Leisure ---
		'leisure:fitness_centre': 'Treningsstudio',
		'leisure:park': 'Park',
		'leisure:playground': 'Lekeplass',
		'leisure:sports_centre': 'Idrettssenter',

		// --- Transport ---
		'pt:nsr_stop': 'Kollektivholdeplass',
		'railway:station': 'Togstasjon',

		// --- Shops ---
		'shop:alcohol': 'Vinmonopol',
		'shop:bakery': 'Bakeri',
		'shop:beauty': 'Skjønnhetssalong',
		'shop:bicycle': 'Sykkelbutikk',
		'shop:books': 'Bokhandel',
		'shop:boutique': 'Boutique',
		'shop:butcher': 'Slakter',
		'shop:charity': 'Bruktbutikk / veldedighet',
		'shop:chemist': 'Apotek',
		'shop:clothes': 'Klesbutikk',
		'shop:confectionery': 'Godteributikk',
		'shop:convenience': 'Nærbutikk',
		'shop:copyshop': 'Kopibutikk',
		'shop:cosmetics': 'Kosmetikkbutikk',
		'shop:deli': 'Delikatessebutikk',
		'shop:dry_cleaning': 'Renseri',
		'shop:e-cigarette': 'E-sigarettbutikk',
		'shop:electrical': 'Elektrobutikk',
		'shop:electronics': 'Elektronikkbutikk',
		'shop:fashion_accessories': 'Motetilbehør',
		'shop:florist': 'Blomsterbutikk',
		'shop:frame': 'Rammebutikk',
		'shop:funeral_directors': 'Begravelsesbyrå',
		'shop:furniture': 'Møbelbutikk',
		'shop:general': 'Butikk',
		'shop:gift': 'Gavebutikk',
		'shop:greengrocer': 'Grønnsakshandler',
		'shop:hairdresser': 'Frisør',
		'shop:hardware': 'Jernvarehandel',
		'shop:hifi': 'Hi-fi-butikk',
		'shop:houseware': 'Kjøkken- og husholdningsbutikk',
		'shop:interior_decoration': 'Interiørbutikk',
		'shop:jewelry': 'Gullsmed',
		'shop:kiosk': 'Kiosk',
		'shop:kitchen': 'Kjøkkenbutikk',
		'shop:leather': 'Lærbutikk',
		'shop:lighting': 'Belysningsbutikk',
		'shop:mall': 'Kjøpesenter',
		'shop:massage': 'Massasje',
		'shop:mobile_phone': 'Mobilbutikk',
		'shop:music': 'Musikkbutikk',
		'shop:musical_instrument': 'Musikkinstrumentbutikk',
		'shop:optician': 'Optiker',
		'shop:perfumery': 'Parfymeri',
		'shop:pet_grooming': 'Dyrepleie',
		'shop:photo': 'Fotobutikk',
		'shop:rental': 'Utleiebutikk',
		'shop:second_hand': 'Bruktbutikk',
		'shop:security': 'Sikkerhetsbutikk',
		'shop:sewing': 'Sybutikk',
		'shop:shoes': 'Skobutikk',
		'shop:sports': 'Sportsbutikk',
		'shop:supermarket': 'Dagligvarebutikk',
		'shop:tailor': 'Skredder',
		'shop:tattoo': 'Tatoveringsstudio',
		'shop:ticket': 'Billettkontor',
		'shop:tobacco': 'Tobakksbutikk',
		'shop:travel_agency': 'Reisebyrå',
		'shop:variety_store': 'Varébutikk',
		'shop:watches': 'Klokkebutikk',
		'shop:weapons': 'Våpenbutikk',
		'shop:yes': 'Butikk',

		// --- Sport & Tourism ---
		'sport:skateboard': 'Skatepark',
		'tourism:hotel': 'Hotell',
		'tourism:museum': 'Museum',
		'tourism:zoo': 'Dyrepark',
	}

	if (type && poiTypeToNorwegian[type]) return poiTypeToNorwegian[type]

	const prefix = type.includes(':') ? type.split(':')[0] : ''
	const fallbackByPrefix = {
		shop: 'Butikk',
		amenity: 'Sted',
		building: 'Bygning',
		leisure: 'Fritid',
		tourism: 'Turisme',
		sport: 'Sport',
		pt: 'Kollektivholdeplass',
		railway: 'Togstasjon',
	}

	if (prefix && fallbackByPrefix[prefix]) return fallbackByPrefix[prefix]

	return ''
}

export const getCategoriesFromTypes = (types = [], name, source) => {
	const categoryMap = {
		airport: 'airport',
		hospital: 'hospital',
		dentist: 'dentist',
		gym: 'gym',
		bar: 'bar',
		office: 'office',
		child_care_agency: 'daycare',
		preschool: 'daycare',
		school: 'school',
		restaurant: 'restaurant',
		shopping_mall: 'shopping_mall',
		grocery_or_supermarket: 'grocery',
		train_station: 'railway_station',
		bus_station: 'bus_station',
		subway_station: 'subway_station',
		parking: 'parking_lot',
		transit_station: 'transit_station',
		sports_activity_location: 'gym',
		grocery_store: 'grocery',
		food_store: 'food',
		pharmacy: 'pharmacy',
		store: 'store',
		movie_theater: 'cinema',
	}

	// Convert types to their respective categories
	const mappedCategories = types.map((type) => categoryMap[type]).filter(Boolean)

	// Create a set of unique category values from the mappedCategories
	const uniqueCategorySet = new Set(mappedCategories)

	// Create an array of all unique categories in the order of categoryMap
	const orderedUniqueCategories = []
	const categoryValues = Object.values(categoryMap)

	// Loop through all possible category values in their defined order
	for (const category of categoryValues) {
		// If this category exists in our uniqueCategorySet, add it to our result
		if (uniqueCategorySet.has(category) && !orderedUniqueCategories.includes(category)) {
			orderedUniqueCategories.push(category)
		}
	}

	// Add daycare category as first category if the name includes 'barnehage'
	if (name && typeof name == 'string' && name.toLowerCase().includes('barnehage')) {
		if (!orderedUniqueCategories.includes('daycare')) {
			orderedUniqueCategories.unshift('daycare')
		}
	}

	return orderedUniqueCategories
}

/**
 * Sets survey answers based on saved responses in a key/value format
 * @param {Object} survey - The survey object to populate with answers
 * @param {Object} responses - An object with question IDs as keys and answer values as values
 */
export function setSurveyAnswersFromSavedResponses(survey, responses) {
	// Validate inputs
	if (!survey || typeof survey !== 'object' || !responses || typeof responses !== 'object') {
		return
	}

	// Check if the survey has an ID and a corresponding response
	if (survey.id && responses.hasOwnProperty(survey.id)) {
		const responseValue = responses[survey.id]

		// Handle questions with options (e.g., multiple-choice or single-choice)
		if (Array.isArray(survey.options)) {
			// Clear previous selections to avoid stale data
			survey.options.forEach((option) => (option.selected = false))

			if (Array.isArray(responseValue)) {
				// Multiple-choice: responseValue is an array of selected values
				const selectedOptions = []
				responseValue.forEach((value) => {
					const option = survey.options.find((opt) => opt.code === value || opt.value === value)
					if (option) {
						option.selected = true
						selectedOptions.push(option)
						// Recursively process sub-surveys if present
						if (option.sub) {
							setSurveyAnswersFromSavedResponses(option.sub, responses)
						}
					}
				})
				survey.answer = selectedOptions
			} else {
				// Single-choice: responseValue is a single value
				const selectedOption = survey.options.find((option) => option.code === responseValue || option.value === responseValue)
				if (selectedOption) {
					selectedOption.selected = true
					survey.answer = selectedOption
					// Recursively process sub-surveys if present
					if (selectedOption.sub) {
						setSurveyAnswersFromSavedResponses(selectedOption.sub, responses)
					}
				} else {
					survey.answer = undefined
				}
			}
		} else {
			// Direct answer (e.g., text input) with no options
			survey.answer = responseValue
		}
	} else {
		// No response for this survey ID, reset selections and answer
		if (Array.isArray(survey.options)) {
			survey.options.forEach((option) => (option.selected = false))
		}
		survey.answer = undefined
	}

	// Recursively process any sub-surveys
	if (survey.sub) {
		setSurveyAnswersFromSavedResponses(survey.sub, responses)
	}
}
